import { ILayoutBaseTestGen } from '../layoutBaseTest';
import {
  PdfGen,
  PdfFont,
  PdfRect,
  PdfBrush,
  ETextAlign,
  PdfUnit,
  PdfSize,
  PageGen,
} from '../../pdf/pdf';
import { Random } from '@educraft/base';
import { OptsBaseTest } from '../optsBaseTest';
import { generateSudoku, ESudokuLevel, SudokuBoard, drawSudokuGrid } from '../../pdf/board/sudoku';

const MM = PdfUnit.FromMillimeters;
const MARGIN = MM(12);
const PAGE_W = PdfSize.A4.width;
const PAGE_H = PdfSize.A4.height;

const LEVEL_NAMES = ['Easy', 'Medium', 'Hard', 'Expert'];

function addPage(pdf: PdfGen, title: string): PageGen {
  const page = pdf.addPage();
  const font = new PdfFont(PdfFont.FONT_NAME, 11);
  page.drawString(
    title,
    font,
    PdfBrush.Black,
    null,
    new PdfRect(MARGIN, MM(5), PAGE_W - 2 * MARGIN, MM(8)),
    ETextAlign.CenterLeft
  );
  return page;
}

function gridSubtitle(page: PageGen, label: string, x: number, y: number, width: number): void {
  const font = new PdfFont(PdfFont.FONT_NAME, 9);
  page.drawString(
    label,
    font,
    PdfBrush.Black,
    null,
    new PdfRect(x, y, width, MM(5)),
    ETextAlign.Center
  );
}

// ── Pages ────────────────────────────────────────────────────────────────────

/**
 * Page 1: 4×4 — all 4 levels, each with puzzle + solution side by side.
 * Layout: 4 rows (one per level), 2 columns (puzzle | solution).
 */
function draw4x4Page(pdf: PdfGen): void {
  const page = addPage(pdf, 'Sudoku — 4×4 (all levels, puzzle + solution)');

  const cellPt = MM(11);
  const gridSize = 4 * cellPt;
  const gFont = new PdfFont(PdfFont.FONT_NAME, 14);
  const sFont = new PdfFont(PdfFont.FONT_NAME, 14);
  const gap = MM(8); // gap between puzzle and solution
  const rowGap = MM(12); // gap between level rows
  const labelH = MM(6);
  const startY = MM(18);

  // Both grids (puzzle+solution) centred on the page
  const pairW = 2 * gridSize + gap;
  const startX = (PAGE_W - pairW) / 2;

  const levels: ESudokuLevel[] = [
    ESudokuLevel.Easy,
    ESudokuLevel.Medium,
    ESudokuLevel.Hard,
    ESudokuLevel.Expert,
  ];

  let y = startY;
  for (let li = 0; li < levels.length; li++) {
    const board = generateSudoku(new Random(100 + li), 4, levels[li]);

    const puzzleX = startX;
    const solX = startX + gridSize + gap;
    const labelY = y;
    const gridY = y + labelH;

    gridSubtitle(page, `${LEVEL_NAMES[li]} — ${board.clueCount} clues`, puzzleX, labelY, gridSize);
    gridSubtitle(page, 'Solution', solX, labelY, gridSize);

    drawSudokuGrid(page, board, puzzleX, gridY, cellPt, gFont, sFont, false);
    drawSudokuGrid(page, board, solX, gridY, cellPt, gFont, sFont, true);

    y += labelH + gridSize + rowGap;
  }
}

/**
 * Pages 2–5: 9×9 — one level per page, puzzle left + solution right.
 */
function draw9x9Page(pdf: PdfGen, level: ESudokuLevel, seed: number): void {
  const page = addPage(pdf, `Sudoku — 9×9  ${LEVEL_NAMES[level]}`);

  const cellPt = MM(9);
  const gridSize = 9 * cellPt;
  const gFont = new PdfFont(PdfFont.FONT_NAME, 11);
  const sFont = new PdfFont(PdfFont.FONT_NAME, 11);
  const gap = PAGE_W - 2 * MARGIN - 2 * gridSize; // remaining space becomes the gap
  const labelH = MM(6);

  const puzzleX = MARGIN;
  const solX = MARGIN + gridSize + gap;
  const gridY = MM(18);

  const board = generateSudoku(new Random(seed), 9, level);

  gridSubtitle(page, `Puzzle  (${board.clueCount} clues)`, puzzleX, gridY - labelH, gridSize);
  gridSubtitle(page, 'Solution', solX, gridY - labelH, gridSize);

  drawSudokuGrid(page, board, puzzleX, gridY, cellPt, gFont, sFont, false);
  drawSudokuGrid(page, board, solX, gridY, cellPt, gFont, sFont, true);
}

/**
 * Pages 6–7: 16×16 — puzzle only, centred (one level per page).
 * 16×16 numbers: 1-9 shown as digits, 10-16 shown as A-G.
 */
function draw16x16Page(pdf: PdfGen, level: ESudokuLevel, seed: number): void {
  const levelName = LEVEL_NAMES[level];
  const page = addPage(pdf, `Sudoku — 16×16  ${levelName}  (values 1–9, A–G)`);

  const cellPt = MM(4.8);
  const gridSize = 16 * cellPt;
  const gFont = new PdfFont(PdfFont.FONT_NAME, 7);
  const sFont = new PdfFont(PdfFont.FONT_NAME, 7);
  const labelH = MM(6);

  const originX = (PAGE_W - gridSize) / 2;
  const gridY = MM(18);

  const board = generateSudoku(new Random(seed), 16, level);

  gridSubtitle(page, `Puzzle  (${board.clueCount} clues given)`, originX, gridY - labelH, gridSize);
  drawSudokuGrid(page, board, originX, gridY, cellPt, gFont, sFont, false);
}

// ── Generator class ──────────────────────────────────────────────────────────

export class TestSudoku implements ILayoutBaseTestGen {
  async generate(pdf: PdfGen, _opts: OptsBaseTest): Promise<void> {
    // Page 1 — 4×4 all levels
    draw4x4Page(pdf);

    // Pages 2–5 — 9×9 per level
    draw9x9Page(pdf, ESudokuLevel.Easy, 200);
    draw9x9Page(pdf, ESudokuLevel.Medium, 201);
    draw9x9Page(pdf, ESudokuLevel.Hard, 202);
    draw9x9Page(pdf, ESudokuLevel.Expert, 203);

    // Pages 6–7 — 16×16 easy + medium
    draw16x16Page(pdf, ESudokuLevel.Easy, 300);
    draw16x16Page(pdf, ESudokuLevel.Medium, 301);
  }
}
